// Measurement display convention — ADR-0018: ordinates to the millimetre, lengths to the decimetre.

export const ORDINATE_FRACTION_DIGITS = 3
export const LENGTH_FRACTION_DIGITS = 1
export const SLOPE_FRACTION_DIGITS = 1
export const DEVIATION_FRACTION_DIGITS = 1

const DECIMAL_SEPARATORS: Record<string, string> = {
	en: '.',
	pl: ',',
}

function localiseSeparator(value: string, locale: string): string {
	const separator = DECIMAL_SEPARATORS[locale.split('-')[0]] ?? ','
	return value.replace('.', separator)
}

function formatFixed(value: number, fractionDigits: number, locale: string): string {
	return localiseSeparator(value.toFixed(fractionDigits), locale)
}

export function formatOrdinate(metres: number, locale = 'pl'): string {
	return formatFixed(metres, ORDINATE_FRACTION_DIGITS, locale)
}

export function formatLength(metres: number, locale = 'pl', fractionDigits = LENGTH_FRACTION_DIGITS): string {
	return formatFixed(metres, fractionDigits, locale)
}

/** A slope (spadek) in per-mille, at the 0.1‰ precision spadek is recorded and read at. */
export function formatSlope(perMille: number, locale = 'pl'): string {
	return formatFixed(perMille, SLOPE_FRACTION_DIGITS, locale)
}

/**
 * A number whose field states no unit and therefore no precision convention — a nominal size, a
 * weight, an angle. Keeps the digits the value carries rather than padding or rounding them.
 */
export function formatPlainNumber(value: number, locale = 'pl'): string {
	return localiseSeparator(String(value), locale)
}

// U+2212, not the hyphen a keyboard produces: the two are indistinguishable in source, so naming it
// keeps a reformat from silently swapping the glyph.
const MINUS_SIGN = '−'

export function formatDeviation(millimetres: number, locale = 'pl'): string {
	const centimetres = formatFixed(Math.abs(millimetres) / 10, DEVIATION_FRACTION_DIGITS, locale)

	return `${millimetres >= 0 ? '+' : MINUS_SIGN}${centimetres} cm`
}

function formatSignedSlope(perMille: number, positiveSign: string, locale: string): string {
	const shown = Number(perMille.toFixed(SLOPE_FRACTION_DIGITS))

	return `${shown < 0 ? MINUS_SIGN : positiveSign}${formatSlope(Math.abs(shown), locale)}`
}

export function formatSlopeDeviation(perMille: number, locale = 'pl'): string {
	return formatSignedSlope(perMille, '+', locale)
}

export function formatSlopeValue(perMille: number, locale = 'pl'): string {
	return formatSignedSlope(perMille, '', locale)
}
